/**
 * Waitlist client service — public marketing site.
 *
 * Thin wrapper over the `joinWaitlist` / `updateWaitlistDetails` callables.
 * Deliberately has no Firestore dependency: the waitlist collection is
 * server-write-only (see firestore.rules), so the client never touches it
 * directly. Everything here runs pre-auth on an unauthenticated visitor.
 *
 * See: marketing/MELIO_WAITLIST_BUILD_TICKETS.md (W-1, W-2)
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { readFirstTouchForSignup } from './acquisitionService';
import type {
  ConfirmWaitlistPaymentResponse,
  JoinWaitlistRequest,
  JoinWaitlistResponse,
  StartWaitlistCheckoutRequest,
  StartWaitlistCheckoutResponse,
  WaitlistInterest,
  WaitlistCaptureSurface,
  WaitlistFocusArea,
  WaitlistReferralMethod,
  WaitlistTiming,
} from '../types/waitlist';

export interface JoinWaitlistInput {
  email: string;
  firstName: string;
  consentMarketingEmail: boolean;
  sourceSurface: WaitlistCaptureSurface;
  /** Carried by the linking CTA, e.g. /waitlist?interest=plus. */
  interest?: WaitlistInterest | null;
  /**
   * Who they say sent them: a code from `?ref=`, or a name from the box. Sent
   * as raw text — the server owns the resolution, so a client cannot nominate
   * which colleague gets the credit.
   */
  referredBy?: string | null;
  referralMethod?: WaitlistReferralMethod | null;
}

export interface WaitlistDetailsInput {
  ref: string;
  outwardCode?: string | null;
  timing?: WaitlistTiming | null;
  household?: boolean | null;
  interest?: WaitlistInterest | null;
  focusAreas?: WaitlistFocusArea[] | null;
}

/**
 * Step 1 — bank the email.
 *
 * The first-touch cookie is forwarded verbatim; the function validates and
 * re-derives every field of it server-side, so a missing or malformed cookie
 * degrades to `channel: 'direct'` rather than failing the submission.
 */
export async function joinWaitlist(
  input: JoinWaitlistInput,
): Promise<JoinWaitlistResponse> {
  const callable = httpsCallable<JoinWaitlistRequest, JoinWaitlistResponse>(
    functions,
    'joinWaitlist',
  );

  try {
    const result = await callable({
      email: input.email,
      firstName: input.firstName,
      consentMarketingEmail: input.consentMarketingEmail,
      sourceSurface: input.sourceSurface,
      interest: input.interest ?? null,
      referredBy: input.referredBy ?? null,
      referralMethod: input.referralMethod ?? null,
      firstTouch: readFirstTouchForSignup() ?? undefined,
    });
    return result.data;
  } catch (err) {
    // Never surface a raw Firebase error to a marketing visitor. The form
    // keeps what they typed and offers a retry.
    console.warn('joinWaitlist failed', err);
    return {
      success: false,
      error: 'We could not save that just now. Please try again.',
    };
  }
}

/**
 * Step 2 — the optional qualifiers, submitted after the email is banked.
 *
 * Failure here is intentionally quiet: the entry already exists and is
 * complete enough to be useful, so there is nothing worth interrupting the
 * visitor about.
 */
export async function updateWaitlistDetails(
  input: WaitlistDetailsInput,
): Promise<boolean> {
  if (!input.ref) return false;

  const callable = httpsCallable<WaitlistDetailsInput, { success: boolean }>(
    functions,
    'updateWaitlistDetails',
  );

  try {
    const result = await callable({
      ref: input.ref,
      outwardCode: input.outwardCode ?? null,
      timing: input.timing ?? null,
      household: input.household ?? null,
      interest: input.interest ?? null,
      focusAreas: input.focusAreas && input.focusAreas.length ? input.focusAreas : null,
    });
    return Boolean(result.data?.success);
  } catch (err) {
    console.warn('updateWaitlistDetails failed', err);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Deposit (2026-09-14)
// ---------------------------------------------------------------------------

const REF_STORAGE_KEY = 'melio_waitlist_ref';

/** Remember the entry so a visitor who backs out of Stripe can resume the deposit. */
export function rememberWaitlistRef(ref: string): void {
  try {
    sessionStorage.setItem(REF_STORAGE_KEY, ref);
  } catch {
    // Storage unavailable (private mode, blocked) — resuming is a convenience only.
  }
}

export function readWaitlistRef(): string | null {
  try {
    return sessionStorage.getItem(REF_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearWaitlistRef(): void {
  try {
    sessionStorage.removeItem(REF_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Full-page navigation to Stripe's hosted Checkout. Separated so tests can stub it. */
export function redirectTo(url: string): void {
  window.location.assign(url);
}

/**
 * Step 2 — the deposit. Asks the server for a Stripe Checkout Session tied to
 * the entry. The server decides the amount; the client only says where Stripe
 * should send the visitor afterwards.
 */
export async function startWaitlistCheckout(
  input: StartWaitlistCheckoutRequest,
): Promise<StartWaitlistCheckoutResponse> {
  const callable = httpsCallable<StartWaitlistCheckoutRequest, StartWaitlistCheckoutResponse>(
    functions,
    'createWaitlistCheckoutSession',
  );
  try {
    const result = await callable(input);
    return result.data;
  } catch (err) {
    console.warn('createWaitlistCheckoutSession failed', err);
    return { success: false, error: 'We could not start the payment just now. Please try again.' };
  }
}

/** Step 3 — back from Stripe: confirm the session server-side before showing anything as reserved. */
export async function confirmWaitlistPayment(sessionId: string): Promise<ConfirmWaitlistPaymentResponse> {
  const callable = httpsCallable<{ sessionId: string }, ConfirmWaitlistPaymentResponse>(
    functions,
    'confirmWaitlistPayment',
  );
  try {
    const result = await callable({ sessionId });
    return result.data;
  } catch (err) {
    console.warn('confirmWaitlistPayment failed', err);
    return { success: false, error: 'We could not confirm that payment just now.' };
  }
}
